import { memo, useCallback } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { useOverlays } from "contexts/OverlayContext";
import { TransactionDirectionType } from "@common/categories";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { useCategoryLabelGetter } from "@hooks/useCategoryLabelGetter";
import { useCategoriesById } from "@hooks/useCategoriesById";
import { useViewTableMeta } from "@hooks/view/useViewTableMeta";
import { Picker } from "@design-system/Picker/Picker.lib";
import { Category } from "@common/types/category";
import { CategoryIcon } from "@components/transactions/CategoryIcon";
import { CategoryCombobox } from "@components/transactions/TransactionSplitV2/CategoryCombobox";
import { TransferMatchLink } from "@components/transactions/TransferMatchLink/TransferMatchLink";
import { AllocationTableOptions } from "../editAllocation/AllocationTable";
import {
    AccrualMatchTableCallbacks,
    MatchableItem,
    MatchItemType,
} from "../types";
import { parseTransactionMatchId } from "../mappers";
import styles from "../AccrualMatchTable.module.scss";
import { HStack } from "designSystem/HStack/HStack";

/**
 * Resolve-difference rows pick the category that absorbs the leftover, the
 * same way the ledger columns pick accounts.
 */
const RemainingCategoryCell = memo(
    ({
        row,
        onCategoryChange,
    }: CellContext<ViewTableFeatures, MatchableItem, unknown> & {
        onCategoryChange:
            | ((id: string, categoryId: string | null) => void)
            | undefined;
    }) => {
        const { categoryId, amount, id } = row.original;
        const categoriesById = useCategoriesById();
        const getCategoryLabel = useCategoryLabelGetter();
        const selectedCategory = categoryId
            ? categoriesById[categoryId]
            : undefined;
        const direction =
            amount > 0
                ? TransactionDirectionType.incoming
                : TransactionDirectionType.outgoing;
        const handleChange = useCallback(
            (category: Category | undefined) => {
                onCategoryChange?.(id, category?.id ?? null);
            },
            [onCategoryChange, id],
        );

        return (
            <ViewTableCell captureClicks className={styles.categoryCell}>
                <CategoryCombobox
                    selectedCategory={selectedCategory}
                    onChange={handleChange}
                    trigger={
                        <Picker.CellContent
                            value={
                                categoryId ? (
                                    <HStack gap={8} align="center">
                                        <CategoryIcon
                                            categoryId={categoryId}
                                            transactionDirection={direction}
                                        />
                                        {getCategoryLabel(
                                            categoryId,
                                            direction,
                                        )}
                                    </HStack>
                                ) : undefined
                            }
                        />
                    }
                />
            </ViewTableCell>
        );
    },
);
RemainingCategoryCell.displayName = "RemainingCategoryCell";

/**
 * Read-only category display for every non-resolve-difference row.
 */
const ReadOnlyCategoryCell = memo(
    ({ row }: CellContext<ViewTableFeatures, MatchableItem, unknown>) => {
        const { categoryId, amount, pairedTransferId, type, id } = row.original;
        const getCategoryLabel = useCategoryLabelGetter();
        const { openOverlay } = useOverlays();
        const isMatched =
            type === MatchItemType.TRANSACTION && pairedTransferId != null;

        if (!categoryId && !isMatched) {
            return <ViewTableCell />;
        }

        const direction =
            amount > 0
                ? TransactionDirectionType.incoming
                : TransactionDirectionType.outgoing;
        const transactionId = isMatched
            ? parseTransactionMatchId(id)
            : undefined;

        return (
            <ViewTableCell
                captureClicks={isMatched}
                className={styles.categoryCell}
            >
                {categoryId ? (
                    <>
                        <CategoryIcon
                            categoryId={categoryId}
                            transactionDirection={direction}
                        />
                        {getCategoryLabel(categoryId, direction)}
                    </>
                ) : null}
                {isMatched && transactionId !== undefined && (
                    <TransferMatchLink
                        onClick={() =>
                            openOverlay("transactionMatch", {
                                transactionId,
                            })
                        }
                        transaction={{ id: transactionId }}
                        matchPreview={row.original.matchPreview}
                    />
                )}
            </ViewTableCell>
        );
    },
);
ReadOnlyCategoryCell.displayName = "ReadOnlyCategoryCell";

/**
 * Category display used by the match drawer for non-GL-first workspaces,
 * which classify transactions by category rather than GL account. Read-only,
 * except on resolve-difference rows.
 */
export const CategoryCell = memo(
    (props: CellContext<ViewTableFeatures, MatchableItem, unknown>) => {
        const meta = useViewTableMeta<
            AccrualMatchTableCallbacks,
            AllocationTableOptions
        >(props.table);
        if (meta.options?.section === "remaining") {
            return (
                <RemainingCategoryCell
                    {...props}
                    onCategoryChange={meta.callbacks?.onRemainingCategoryChange}
                />
            );
        }
        return <ReadOnlyCategoryCell {...props} />;
    },
);
ReadOnlyCategoryCell.displayName = "ReadOnlyCategoryCell";
